import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { SEOHead } from '@/components/seo/SEOHead';
import { PageHero } from '@/components/common/PageHero';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ExternalLink, Loader2, ArrowLeft, Award, ChevronRight } from 'lucide-react';
import { useSiteContent } from '@/context/SiteContentContext';
import NotFound from './NotFound';

interface Brand {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  description: string | null;
  short_description: string | null;
  content: string | null;
  attributes: { key: string; value: string }[] | null;
  images: string[] | null;
  website_url: string | null;
  is_active: boolean;
  is_featured: boolean;
  is_published: boolean;
  meta_title: string | null;
  meta_description: string | null;
}

export default function BrandDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const siteContent = useSiteContent();
  const companyName = siteContent.template?.brand?.name || 'Our Company';

  const { data: brand, isLoading } = useQuery({
    queryKey: ['public-brand', slug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('brands')
        .select('*')
        .eq('slug', slug!)
        .eq('is_published', true)
        .eq('is_active', true)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as Brand | null;
    },
    enabled: !!slug,
  });

  const { data: otherBrands = [] } = useQuery({
    queryKey: ['public-brands-sidebar'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('brands')
        .select('id, name, slug, logo_url, short_description')
        .eq('is_published', true)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(12);
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!brand) return <NotFound />;

  const attributes = Array.isArray(brand.attributes) ? brand.attributes : [];
  const images = Array.isArray(brand.images) ? brand.images : [];
  const filteredOtherBrands = otherBrands.filter(b => b.id !== brand.id);

  return (
    <>
      <SEOHead
        title={brand.meta_title || `${brand.name} | ${companyName}`}
        description={brand.meta_description || brand.short_description || brand.description?.slice(0, 155) || `Learn about ${brand.name} products and services at ${companyName}.`}
      />

      <PageHero
        title={brand.name}
        subtitle={brand.short_description || brand.description || undefined}
        badge="Brand Partner"
      />

      <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Logo + intro */}
            {brand.logo_url && (
              <div className="bg-muted/30 rounded-2xl p-8 flex items-center justify-center">
                <img
                  src={brand.logo_url}
                  alt={`${brand.name} logo`}
                  className="max-h-24 object-contain"
                />
              </div>
            )}

            {/* Rich content */}
            {brand.content && (
              <div className="prose prose-neutral dark:prose-invert max-w-none">
                {brand.content.split(/\n\n+/).map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            )}

            {/* Description fallback */}
            {!brand.content && brand.description && (
              <div className="prose prose-neutral dark:prose-invert max-w-none">
                <p>{brand.description}</p>
              </div>
            )}

            {/* Attributes */}
            {attributes.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                    <Award className="h-5 w-5 text-primary" />
                    Brand Highlights
                  </h3>
                  <dl className="grid sm:grid-cols-2 gap-4">
                    {attributes.map((attr, i) => (
                      <div key={i} className="bg-muted/40 rounded-lg p-3">
                        <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                          {attr.key}
                        </dt>
                        <dd className="text-sm font-medium">{attr.value}</dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            )}

            {/* Image gallery */}
            {images.length > 0 && (
              <div>
                <h3 className="font-heading font-semibold text-lg mb-4">Gallery</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {images.map((src, i) => (
                    <div key={i} className="rounded-xl overflow-hidden bg-muted/30">
                      <img
                        src={src}
                        alt={`${brand.name} image ${i + 1}`}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {brand.website_url && (
              <div>
                <a href={brand.website_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="gap-2">
                    <ExternalLink className="h-4 w-4" />
                    Visit {brand.name} Website
                  </Button>
                </a>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            {brand.is_featured && (
              <Badge className="mb-2">Featured Partner</Badge>
            )}

            <Card>
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold mb-1">Need Service?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  We are an authorized {brand.name} dealer. Contact us for installation, repair, or maintenance.
                </p>
                <Link to="/contact">
                  <Button className="w-full">Get a Quote</Button>
                </Link>
              </CardContent>
            </Card>

            {filteredOtherBrands.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold mb-4">Other Brands We Carry</h3>
                  <div className="space-y-2">
                    {filteredOtherBrands.slice(0, 8).map(b => (
                      <Link
                        key={b.id}
                        to={`/brands/${b.slug}`}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors group"
                      >
                        {b.logo_url ? (
                          <img src={b.logo_url} alt={b.name} className="h-6 w-6 object-contain" />
                        ) : (
                          <Award className="h-5 w-5 text-muted-foreground" />
                        )}
                        <span className="text-sm font-medium group-hover:text-primary transition-colors flex-1">
                          {b.name}
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </aside>
        </div>
      </div>
    </>
  );
}
